import {
   createSelector,
   createSlice,
   createAsyncThunk,
   PayloadAction,
   createEntityAdapter,
} from "@reduxjs/toolkit"
import { RootState } from "."
import { ApiError, ApiResponse } from "@/api"
import { AlertResponse } from "@/api/AlertsService"
import { CellResponse, requestCells } from "@/api/CellsService"
import { requestUpdateFacilityLevel } from "@/api/FacilityLevelsService"
import { SensorType } from "@/api/SensorsService"
import { WsFeedPayload } from "@/hooks/useRealtimeConnection"
import {
   realtimeFeedReceived,
   realtimeFeedsReceived,
} from "@/store/websocketSlice"
import { Alert } from "@/types/alert"
import { Cell } from "@/types/cell"
import { FacilityDetails, Level, LevelForm } from "@/types/facility"
import { LevelConfigurationItemMode } from "@/types/levelConfiguration"
import { processThresholds } from "@/utils/alerts"
import { mapWsFeedToCell } from "@/utils/cells"
import { groupBy, isEqual } from "@/utils/lodash"
import { getSensor } from "@/utils/sensors"

const cellsAdapter = createEntityAdapter({
   selectId: (cell: Cell) => cell.id,
})

interface CellsState {
   loading: boolean
   error: number | null
}

export const initialState: CellsState &
   ReturnType<typeof cellsAdapter.getInitialState> = {
   ...cellsAdapter.getInitialState(),
   loading: false,
   error: null,
}

// unused — WS feeds cells; keep until WS is confirmed stable
export const fetchCells = createAsyncThunk(
   "cells/fetchCells",
   async (
      {
         levelId,
      }: {
         levelId: string
      },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<CellResponse[]> =
            await requestCells(levelId)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const updateLevelByCell = createAsyncThunk(
   "cells/updateLevelByCell",
   async (
      {
         levelId,
         levelForm,
      }: { levelId: string; levelForm: Partial<LevelForm> },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<Level> = await requestUpdateFacilityLevel(
            levelId,
            levelForm
         )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

const cellsSlice = createSlice({
   name: "cells",
   initialState,
   reducers: {
      resetCells: () => initialState,
      seedCellsFromLevels: (
         state,
         action: PayloadAction<{
            levels: FacilityDetails["levels"]
            parentLevelId: string
         }>
      ) => {
         const { levels, parentLevelId } = action.payload
         const cells: Cell[] = levels
            .filter((level) => level.parents?.includes(parentLevelId))
            .filter(
               (level) =>
                  level.type.mode === LevelConfigurationItemMode.PHYSICAL
            )
            .map((level) => {
               const existing = state.entities[level.id]
               return {
                  id: level.id,
                  name: level.name,
                  hasCustomConfigurations: level.hasCustomConfigurations,
                  vacant: false,
                  maintenance: false,
                  alerts: existing?.alerts ?? [],
                  data: existing?.data ?? {
                     status: {
                        humanPresence: false,
                        sensorConnected: false,
                        moving: false,
                     },
                     vitals: null,
                  },
                  hasDoorSensor: !!getSensor(level.sensors, SensorType.DOOR),
                  doorSensorData: null,
               }
            })
         cellsAdapter.setAll(state, cells)
      },
      removeAlertById: (state, action) => {
         const { alertId } = action.payload
         const cellId = Object.keys(state.entities).find((id) => {
            const cell = state.entities[id]
            return cell?.alerts.some((alert) => alert.id === alertId)
         })
         if (!cellId) return
         const cell = state.entities[cellId]
         if (cell) {
            cellsAdapter.updateOne(state, {
               id: cellId,
               changes: {
                  alerts: cell.alerts.filter((alert) => alert.id !== alertId),
               },
            })
         }
      },
      removeAlerts: (state) => {
         const updates = cellsAdapter
            .getSelectors()
            .selectAll(state)
            .map((cell) => {
               if (!cell) return null
               return {
                  ...cell,
                  alerts: [],
               } as Cell
            })
            .filter((cell: Cell | null) => cell !== null)

         cellsAdapter.upsertMany(state, updates)
      },
      setCellCustomConfigurations: (
         state,
         action: PayloadAction<{
            levelId: string
            hasCustomConfigurations: boolean
         }>
      ) => {
         const { levelId, hasCustomConfigurations } = action.payload
         if (state.entities[levelId])
            cellsAdapter.updateOne(state, {
               id: levelId,
               changes: { hasCustomConfigurations },
            })
      },
      updateCellsByAlerts: (state, action) => {
         // group incoming alerts by cell id
         const groupedUpdates = groupBy(
            action.payload.response.data as AlertResponse[],
            (alert: AlertResponse) => alert.level.id
         )

         const mapAlert = (alertData: AlertResponse): Alert =>
            ({
               id: alertData.id,
               level: alertData.level,
               priority: alertData.priority,
               timestamp: alertData.timestamp,
               vitals: alertData.vitals,
               alertConfiguration: alertData.alertConfiguration,
               ...processThresholds(alertData.thresholds),
            }) as Alert

         // Build updates for every existing cell: set alerts to mapped group or empty array
         const updates = Object.values(state.entities).reduce(
            (acc: Cell[], cell) => {
               if (!cell) return acc
               const alertsArray = groupedUpdates[cell.id] ?? []
               const newAlerts = alertsArray.map(mapAlert)

               // cheap equality check by ids to avoid unnecessary updates
               const prevIds = (cell.alerts || [])
                  .map((a) => a.id)
                  .sort()
                  .join(",")
               const newIds = newAlerts
                  .map((a) => a.id)
                  .sort()
                  .join(",")
               if (prevIds !== newIds) {
                  acc.push({
                     ...cell,
                     alerts: newAlerts,
                  } as Cell)
               }
               return acc
            },
            [] as Cell[]
         )

         if (updates.length > 0) {
            cellsAdapter.upsertMany(state, updates)
         }
      },
   },
   extraReducers: (builder) => {
      const applyRealtimeFeed = (
         state: typeof initialState,
         payload: {
            entityId: string
            rawPayload: unknown
         }
      ) => {
         const { entityId: cellId, rawPayload } = payload
         const cell = state.entities[cellId]
         if (!cell) return

         cellsAdapter.updateOne(state, {
            id: cellId,
            changes: mapWsFeedToCell(cell, rawPayload as WsFeedPayload),
         })
      }

      builder
         .addCase(fetchCells.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(
            fetchCells.fulfilled,
            (state, action: PayloadAction<ApiResponse<CellResponse[]>>) => {
               state.loading = false
               // map API response to Cell, preserving any existing alerts in state
               const cells = action.payload.data.map((cell) => {
                  const existing = state.entities[cell.id]
                  return {
                     id: cell.id,
                     name: cell.name,
                     data: {
                        status: {
                           humanPresence: cell.data.status.humanPresence,
                           sensorConnected: cell.data.status.sensorConnected,
                           moving: cell.data.status.moving,
                        },
                        vitals: cell.data.vitals,
                     },
                     hasCustomConfigurations: cell.hasCustomConfigurations,
                     vacant: cell.vacant,
                     maintenance: cell.maintenance,
                     alerts: existing?.alerts ?? [],
                  } as Cell
               })
               cellsAdapter.setAll(state, cells)
            }
         )
         .addCase(fetchCells.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(updateLevelByCell.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(updateLevelByCell.fulfilled, (state, action) => {
            state.loading = false
            const updatedLevel = action.payload.data
            const { levelForm } = action.meta.arg
            Object.values(state.entities).forEach((cell) => {
               if (cell?.id === updatedLevel.id) {
                  cellsAdapter.updateOne(state, {
                     id: cell.id,
                     changes: {
                        ...levelForm,
                     },
                  })
               }
            })
         })
         .addCase(updateLevelByCell.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(realtimeFeedReceived, (state, action) => {
            applyRealtimeFeed(state, action.payload)
         })
         .addCase(realtimeFeedsReceived, (state, action) => {
            const latestByEntityId = new Map<
               string,
               (typeof action.payload)[number]
            >()

            for (const payload of action.payload) {
               latestByEntityId.set(payload.entityId, payload)
            }

            for (const payload of latestByEntityId.values()) {
               applyRealtimeFeed(state, payload)
            }
         })
   },
})

export const {
   resetCells,
   seedCellsFromLevels,
   removeAlertById,
   removeAlerts,
   updateCellsByAlerts,
   setCellCustomConfigurations,
} = cellsSlice.actions
export default cellsSlice.reducer
export const cellsSelectors = cellsAdapter.getSelectors<RootState>(
   (state) => state.cellsReducer
)

export const selectSortedCellIds = createSelector(
   cellsSelectors.selectAll,
   (cells) =>
      [...cells]
         .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, {
               numeric: true,
               sensitivity: "base",
            })
         )
         .map((cell) => cell.id),
   { memoizeOptions: { resultEqualityCheck: isEqual } }
)

export const selectCellIdsWithActiveAlerts = createSelector(
   [cellsSelectors.selectAll],
   (cells): Set<string> => {
      const idsWithActiveAlerts = new Set<string>()
      cells.forEach((cell) => {
         if (cell.alerts && cell.alerts.length > 0) {
            idsWithActiveAlerts.add(cell.id)
         }
      })
      return idsWithActiveAlerts
   },
   { memoizeOptions: { resultEqualityCheck: isEqual } }
)

export const selectCellTotals = createSelector(
   cellsSelectors.selectAll,
   (cells) => {
      return cells.reduce(
         (totals, cell) => {
            if (cell.hasCustomConfigurations) {
               totals.totalCustomCells++
            } else {
               totals.totalDefaultCells++
            }
            return totals
         },
         { totalDefaultCells: 0, totalCustomCells: 0 }
      )
   }
)
